import React, { useRef, useState } from 'react';
import { Check, Download, Upload, X } from 'lucide-react';

const BecomePartner = () => {
  const [profileType, setProfileType] = useState('');
  const [formData, setFormData] = useState({
    // Common fields
    yearsExperience: '',
    country: '',
    
    // Independent driver fields
    fullName: '',
    email: '',
    phone: '',
    residence: '',
    
    // Company fields
    companyName: '',
    responsibleName: '',
    companyPhone: '',
    companyEmail: '',
    numberOfDrivers: '',
    companyCountry: '',
    
    // Additional fields for BENIN
    interventionZones: [],
    address: '',
    availability: [],
    availabilityOther: '',
    transportMean: '',
    transportMeanOther: '',
    
    // File uploads
    proofOfResidence: null,
    commercialRegister: null,
    idDocument: null
  });

  const [fileNames, setFileNames] = useState({
    proofOfResidence: '',
    commercialRegister: '',
    idDocument: ''
  });

  const [errors, setErrors] = useState({});
  const successRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleProfileTypeChange = (type) => {
    setProfileType(type);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear intervention zones when country changes
    if ((name === 'residence' || name === 'companyCountry') && 
        (value === 'BENIN' || value === 'TOGO') &&
        (formData[name] !== value)) {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        interventionZones: [] // Reset zones when country changes
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCheckboxChange = (e, fieldName) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      const currentValues = prev[fieldName] || [];
      if (checked) {
        return { ...prev, [fieldName]: [...currentValues, value] };
      } else {
        return { ...prev, [fieldName]: currentValues.filter(v => v !== value) };
      }
    });
    // Clear error for this field
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          [fieldName]: 'Le fichier ne doit pas dépasser 5 MB'
        }));
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        [fieldName]: file
      }));
      setFileNames(prev => ({
        ...prev,
        [fieldName]: file.name
      }));
      // Clear error for this field
      if (errors[fieldName]) {
        setErrors(prev => ({
          ...prev,
          [fieldName]: ''
        }));
      }
    }
  };

  const removeFile = (fieldName) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: null
    }));
    setFileNames(prev => ({
      ...prev,
      [fieldName]: ''
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!profileType) {
      newErrors.profileType = 'Veuillez sélectionner un profil';
      return newErrors;
    }

    if (profileType === 'independent') {
      if (!formData.fullName.trim()) newErrors.fullName = 'Ce champ est requis';
      if (!formData.email.trim()) {
        newErrors.email = 'Ce champ est requis';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email invalide';
      }
      if (!formData.phone.trim()) newErrors.phone = 'Ce champ est requis';
      if (!formData.yearsExperience) newErrors.yearsExperience = 'Ce champ est requis';
      if (!formData.residence) newErrors.residence = 'Ce champ est requis';
    } else if (profileType === 'company') {
      if (!formData.companyName.trim()) newErrors.companyName = 'Ce champ est requis';
      if (!formData.responsibleName.trim()) newErrors.responsibleName = 'Ce champ est requis';
      if (!formData.companyPhone.trim()) newErrors.companyPhone = 'Ce champ est requis';
      if (!formData.companyEmail.trim()) {
        newErrors.companyEmail = 'Ce champ est requis';
      } else if (!/\S+@\S+\.\S+/.test(formData.companyEmail)) {
        newErrors.companyEmail = 'Email invalide';
      }
      if (!formData.numberOfDrivers) newErrors.numberOfDrivers = 'Ce champ est requis';
      if (!formData.yearsExperience) newErrors.yearsExperience = 'Ce champ est requis';
      if (!formData.companyCountry) newErrors.companyCountry = 'Ce champ est requis';
    }

    // Additional validation for BENIN and TOGO zones
    if (formData.residence === 'BENIN' || formData.companyCountry === 'BENIN' ||
        formData.residence === 'TOGO' || formData.companyCountry === 'TOGO') {
      if (!formData.interventionZones || formData.interventionZones.length === 0) {
        newErrors.interventionZones = 'Veuillez sélectionner au moins une zone';
      }
      if (!formData.address.trim()) newErrors.address = 'Ce champ est requis';
      if (!formData.availability || formData.availability.length === 0) {
        newErrors.availability = 'Veuillez sélectionner au moins une disponibilité';
      }
      if (formData.availability.includes('Other') && !formData.availabilityOther.trim()) {
        newErrors.availabilityOther = 'Veuillez préciser';
      }
      if (!formData.transportMean) newErrors.transportMean = 'Ce champ est requis';
      if (formData.transportMean === 'Other' && !formData.transportMeanOther.trim()) {
        newErrors.transportMeanOther = 'Veuillez préciser';
      }
      
      // File uploads required only for companies
      if (profileType === 'company') {
        if (!formData.proofOfResidence) newErrors.proofOfResidence = 'Ce document est requis';
        if (!formData.commercialRegister) newErrors.commercialRegister = 'Ce document est requis';
        if (!formData.idDocument) newErrors.idDocument = 'Ce document est requis';
      }
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Scroll to first error
      const firstErrorField = document.querySelector('.border-red-500');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsSubmitting(true);

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      
      // Add profile type
      formDataToSend.append('profileType', profileType);
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          if (Array.isArray(formData[key])) {
            formDataToSend.append(key, JSON.stringify(formData[key]));
          } else if (formData[key] instanceof File) {
            formDataToSend.append(key, formData[key]);
          } else {
            formDataToSend.append(key, formData[key]);
          }
        }
      });

      // Send to API endpoint
      const apiEndpoint = process.env.REACT_APP_PARTNER_API_ENDPOINT || '/api/partner/register';
      
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi du formulaire');
      }

      const result = await response.json();
      console.log('Form submitted successfully:', result);

      setSubmitSuccess(true);
      setTimeout(() => {
        successRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ submit: 'Une erreur est survenue. Veuillez réessayer.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Devenir Partenaire
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            Ce formulaire a pour objectif de recueillir les informations nécessaires afin de vous intégrer à notre réseau de partenaires de livraison, <span className="font-semibold text-orange-600">OuiGoo Partner</span>.
          </p>
          <p className="text-gray-600 mt-4">
            Vos réponses nous permettront de mieux comprendre votre profil (livreur indépendant ou société), vos zones d'intervention et vos capacités, afin de vous proposer des missions adaptées et de vous connecter aux e-commerçants qui utilisent notre plateforme.
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {submitSuccess ? (
            <div ref={successRef} className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Merci pour votre candidature !
              </h2>
              <p className="text-gray-600">
                Installez l'application livreur OuiGoo et suivez les étapes pour démarrer.
              </p>
              {profileType === 'independent' && (
                <a
                  href={process.env.REACT_APP_DRIVER_APP_URL}
                  download
                  className="inline-flex items-center mt-6 bg-primary-custom hover:bg-primary-custom-dark text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Télécharger l'application
                </a>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Profile Type Selection */}
              <div className="mb-8">
                <label className="block text-lg font-semibold text-gray-900 mb-4">
                  Quel est votre profil ? <span className="text-red-500">*</span>
                </label>
                
                <div className="space-y-3">
                  <label className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    profileType === 'independent' 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-gray-200 hover:border-orange-300'
                  }`}>
                    <input
                      type="radio"
                      name="profileType"
                      value="independent"
                      checked={profileType === 'independent'}
                      onChange={(e) => handleProfileTypeChange(e.target.value)}
                      className="mt-1 w-4 h-4 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="ml-3 text-gray-900">
                      Je suis un livreur indépendant (je livre avec ma propre moto)
                    </span>
                  </label>

                  <label className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    profileType === 'company' 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-gray-200 hover:border-orange-300'
                  }`}>
                    <input
                      type="radio"
                      name="profileType"
                      value="company"
                      checked={profileType === 'company'}
                      onChange={(e) => handleProfileTypeChange(e.target.value)}
                      className="mt-1 w-4 h-4 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="ml-3 text-gray-900">
                      J'ai une société de livraison (avec plusieurs livreurs)
                    </span>
                  </label>
                </div>
                
                {errors.profileType && (
                  <p className="mt-2 text-sm text-red-600">{errors.profileType}</p>
                )}
              </div>

              {/* Independent Driver Form */}
              {profileType === 'independent' && (
                <div className="space-y-6 animate-fadeIn">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-orange-500">
                    Livreur indépendant
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom et prénom <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.fullName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Votre nom complet"
                    />
                    {errors.fullName && (
                      <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="votre.email@exemple.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Numéro de téléphone (WhatsApp) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="XXXXXXXX"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Année d'expérience <span className="text-red-500">*</span>
                    </label>
                    <p className="text-sm text-gray-500 mb-2">
                      Depuis combien de temps faites vous de la livraison?
                    </p>
                    <input
                      type="number"
                      name="yearsExperience"
                      value={formData.yearsExperience}
                      onChange={handleInputChange}
                      min="0"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.yearsExperience ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Nombre d'années"
                    />
                    {errors.yearsExperience && (
                      <p className="mt-1 text-sm text-red-600">{errors.yearsExperience}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lieu de résidence <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="residence"
                      value={formData.residence}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.residence ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Sélectionnez un pays</option>
                      <option value="BENIN">BENIN</option>
                      <option value="TOGO">TOGO</option>
                    </select>
                    {errors.residence && (
                      <p className="mt-1 text-sm text-red-600">{errors.residence}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Company Form */}
              {profileType === 'company' && (
                <div className="space-y-6 animate-fadeIn">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-orange-500">
                    Société de livraison
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom de la société <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.companyName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Nom de votre société"
                    />
                    {errors.companyName && (
                      <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom du responsable <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="responsibleName"
                      value={formData.responsibleName}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.responsibleName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Nom du responsable"
                    />
                    {errors.responsibleName && (
                      <p className="mt-1 text-sm text-red-600">{errors.responsibleName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Numéro de téléphone (WhatsApp) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="companyPhone"
                      value={formData.companyPhone}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.companyPhone ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="+229 XX XX XX XX"
                    />
                    {errors.companyPhone && (
                      <p className="mt-1 text-sm text-red-600">{errors.companyPhone}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Adresse email professionnelle <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="companyEmail"
                      value={formData.companyEmail}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.companyEmail ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="contact@societe.com"
                    />
                    {errors.companyEmail && (
                      <p className="mt-1 text-sm text-red-600">{errors.companyEmail}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre de livreurs disponibles <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="numberOfDrivers"
                      value={formData.numberOfDrivers}
                      onChange={handleInputChange}
                      min="1"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.numberOfDrivers ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Nombre de livreurs"
                    />
                    {errors.numberOfDrivers && (
                      <p className="mt-1 text-sm text-red-600">{errors.numberOfDrivers}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Année d'expérience <span className="text-red-500">*</span>
                    </label>
                    <p className="text-sm text-gray-500 mb-2">
                      Depuis combien de temps faites vous de la livraison?
                    </p>
                    <input
                      type="number"
                      name="yearsExperience"
                      value={formData.yearsExperience}
                      onChange={handleInputChange}
                      min="0"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.yearsExperience ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Nombre d'années"
                    />
                    {errors.yearsExperience && (
                      <p className="mt-1 text-sm text-red-600">{errors.yearsExperience}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pays de résidence <span className="text-red-500">*</span>
                    </label>
                    <p className="text-sm text-gray-500 mb-2">
                      Choisissez le pays où vous vous trouver
                    </p>
                    <select
                      name="companyCountry"
                      value={formData.companyCountry}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.companyCountry ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Sélectionnez un pays</option>
                      <option value="BENIN">BENIN</option>
                      <option value="TOGO">TOGO</option>
                    </select>
                    {errors.companyCountry && (
                      <p className="mt-1 text-sm text-red-600">{errors.companyCountry}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Fields for BENIN and TOGO */}
              {(formData.residence === 'BENIN' || formData.companyCountry === 'BENIN' || 
                formData.residence === 'TOGO' || formData.companyCountry === 'TOGO') && (
                <div className="space-y-6 animate-fadeIn mt-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-orange-500">
                    Zone {formData.residence || formData.companyCountry} - Informations complémentaires
                  </h3>

                  {/* Intervention Zones */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Zones d'intervention <span className="text-red-500">*</span>
                    </label>
                    <p className="text-sm text-gray-500 mb-3">
                      Les zones où vous pouvez effectuer des livraisons
                    </p>
                    <div className="space-y-2">
                      {((formData.residence === 'BENIN' || formData.companyCountry === 'BENIN')
                        ? ['Cotonou', 'Abomey Calavi', 'Porto Novo']
                        : ['Lomé', 'Kpalime', 'Kara', 'Sokodé']
                      ).map((zone) => (
                        <label key={zone} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            value={zone}
                            checked={formData.interventionZones.includes(zone)}
                            onChange={(e) => handleCheckboxChange(e, 'interventionZones')}
                            className="w-4 h-4 text-orange-600 focus:ring-orange-500 rounded"
                          />
                          <span className="ml-3 text-gray-900">{zone}</span>
                        </label>
                      ))}
                    </div>
                    {errors.interventionZones && (
                      <p className="mt-1 text-sm text-red-600">{errors.interventionZones}</p>
                    )}
                  </div>

                  {/* Address */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Adresse <span className="text-red-500">*</span>
                    </label>
                    <p className="text-sm text-gray-500 mb-2">
                      Votre adresse de résidence
                    </p>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      rows="3"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.address ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Votre adresse complète"
                    />
                    {errors.address && (
                      <p className="mt-1 text-sm text-red-600">{errors.address}</p>
                    )}
                  </div>

                  {/* Availability */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Disponibilités <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      {[
                        'Lundi - Vendredi (08h-18h)',
                        'Lundi - Vendredi (18h-22h)',
                        'Week-ends (Samedi et Dimanche)',
                        '24h/24 et 7j/7'
                      ].map((option) => (
                        <label key={option} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            value={option}
                            checked={formData.availability.includes(option)}
                            onChange={(e) => handleCheckboxChange(e, 'availability')}
                            className="w-4 h-4 text-orange-600 focus:ring-orange-500 rounded"
                          />
                          <span className="ml-3 text-gray-900">{option}</span>
                        </label>
                      ))}
                      <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          value="Other"
                          checked={formData.availability.includes('Other')}
                          onChange={(e) => handleCheckboxChange(e, 'availability')}
                          className="w-4 h-4 text-orange-600 focus:ring-orange-500 rounded"
                        />
                        <span className="ml-3 text-gray-900">Autre:</span>
                      </label>
                      {formData.availability.includes('Other') && (
                        <input
                          type="text"
                          name="availabilityOther"
                          value={formData.availabilityOther}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                            errors.availabilityOther ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Précisez votre disponibilité"
                        />
                      )}
                    </div>
                    {errors.availability && (
                      <p className="mt-1 text-sm text-red-600">{errors.availability}</p>
                    )}
                    {errors.availabilityOther && (
                      <p className="mt-1 text-sm text-red-600">{errors.availabilityOther}</p>
                    )}
                  </div>

                  {/* Transport Mean */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Moyen de transport utilisé <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      {['Moto', 'Moto électrique'].map((option) => (
                        <label key={option} className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.transportMean === option
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-orange-300'
                        }`}>
                          <input
                            type="radio"
                            name="transportMean"
                            value={option}
                            checked={formData.transportMean === option}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-orange-600 focus:ring-orange-500"
                          />
                          <span className="ml-3 text-gray-900">{option}</span>
                        </label>
                      ))}
                      <label className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.transportMean === 'Other'
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-orange-300'
                      }`}>
                        <input
                          type="radio"
                          name="transportMean"
                          value="Other"
                          checked={formData.transportMean === 'Other'}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="ml-3 text-gray-900">Autre:</span>
                      </label>
                      {formData.transportMean === 'Other' && (
                        <input
                          type="text"
                          name="transportMeanOther"
                          value={formData.transportMeanOther}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                            errors.transportMeanOther ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Précisez le moyen de transport"
                        />
                      )}
                    </div>
                    {errors.transportMean && (
                      <p className="mt-1 text-sm text-red-600">{errors.transportMean}</p>
                    )}
                    {errors.transportMeanOther && (
                      <p className="mt-1 text-sm text-red-600">{errors.transportMeanOther}</p>
                    )}
                  </div>

                  {/* File Uploads Section - Only for companies */}
                  {profileType === 'company' && (
                    <div className="pt-6 border-t border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">
                        Documents requis
                      </h4>

                    {/* Proof of Residence */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preuve de résidence <span className="text-red-500">*</span>
                      </label>
                      <p className="text-sm text-gray-500 mb-3">
                        Ajoutez une pièce où l'on peut voir clairement votre adresse et votre nom (Max 5 MB)
                      </p>
                      {!fileNames.proofOfResidence ? (
                        <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                          errors.proofOfResidence ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}>
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <Upload className="w-8 h-8 mb-2 text-gray-400" />
                            <p className="text-sm text-gray-600">
                              <span className="font-semibold">Cliquez pour télécharger</span> ou glissez-déposez
                            </p>
                            <p className="text-xs text-gray-500 mt-1">PDF, PNG, JPG (MAX. 5MB)</p>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.png,.jpg,.jpeg"
                            onChange={(e) => handleFileChange(e, 'proofOfResidence')}
                          />
                        </label>
                      ) : (
                        <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center">
                            <Check className="w-5 h-5 text-green-600 mr-2" />
                            <span className="text-sm text-gray-900">{fileNames.proofOfResidence}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile('proofOfResidence')}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                      {errors.proofOfResidence && (
                        <p className="mt-1 text-sm text-red-600">{errors.proofOfResidence}</p>
                      )}
                    </div>

                    {/* Commercial Register / IFU */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Registre de Commerce / IFU <span className="text-red-500">*</span>
                      </label>
                      <p className="text-sm text-gray-500 mb-3">
                        {profileType === 'company' 
                          ? "Le Registre de Commerce ou l'IFU de l'entreprise (Max 5 MB)"
                          : "Le Registre de Commerce ou l'IFU à votre nom (Max 5 MB)"
                        }
                      </p>
                      {!fileNames.commercialRegister ? (
                        <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                          errors.commercialRegister ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}>
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <Upload className="w-8 h-8 mb-2 text-gray-400" />
                            <p className="text-sm text-gray-600">
                              <span className="font-semibold">Cliquez pour télécharger</span> ou glissez-déposez
                            </p>
                            <p className="text-xs text-gray-500 mt-1">PDF, PNG, JPG (MAX. 5MB)</p>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.png,.jpg,.jpeg"
                            onChange={(e) => handleFileChange(e, 'commercialRegister')}
                          />
                        </label>
                      ) : (
                        <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center">
                            <Check className="w-5 h-5 text-green-600 mr-2" />
                            <span className="text-sm text-gray-900">{fileNames.commercialRegister}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile('commercialRegister')}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                      {errors.commercialRegister && (
                        <p className="mt-1 text-sm text-red-600">{errors.commercialRegister}</p>
                      )}
                    </div>

                    {/* ID Document */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pièce d'identité du Représentant <span className="text-red-500">*</span>
                      </label>
                      <p className="text-sm text-gray-500 mb-3">
                        {profileType === 'company'
                          ? "Pièce d'identité du responsable légal ou gérant de l'entreprise (Max 5 MB)"
                          : "Votre propre pièce d'identité (CNI, passeport ou autre document officiel) (Max 5 MB)"
                        }
                      </p>
                      {!fileNames.idDocument ? (
                        <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                          errors.idDocument ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}>
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <Upload className="w-8 h-8 mb-2 text-gray-400" />
                            <p className="text-sm text-gray-600">
                              <span className="font-semibold">Cliquez pour télécharger</span> ou glissez-déposez
                            </p>
                            <p className="text-xs text-gray-500 mt-1">PDF, PNG, JPG (MAX. 5MB)</p>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.png,.jpg,.jpeg"
                            onChange={(e) => handleFileChange(e, 'idDocument')}
                          />
                        </label>
                      ) : (
                        <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center">
                            <Check className="w-5 h-5 text-green-600 mr-2" />
                            <span className="text-sm text-gray-900">{fileNames.idDocument}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile('idDocument')}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                      {errors.idDocument && (
                        <p className="mt-1 text-sm text-red-600">{errors.idDocument}</p>
                      )}
                    </div>
                    </div>
                  )}
                </div>
              )}

              {/* Submit Button */}
              {profileType && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  {errors.submit && (
                    <p className="mb-4 text-sm text-red-600 text-center">{errors.submit}</p>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary-custom hover:bg-primary-custom-dark text-white font-semibold py-4 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Envoi en cours...
                      </>
                    ) : (
                      'Soumettre ma candidature'
                    )}
                  </button>
                </div>
              )}
            </form>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default BecomePartner;
